import { useState } from "react";
import { Calendar, Instagram, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { createBooking } from "../lib/api";

const BOOKSY_URL = "https://booksy.com/en-us/";
const IG_URL = "https://instagram.com/tatzbyb4nk";

const initialForm = {
    name: "",
    email: "",
    phone: "",
    idea: "",
    placement: "",
    size: "",
    budget: "",
};

export const Booking = () => {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.idea) {
            toast.error("Name, email and tattoo idea are required.");
            return;
        }
        setSubmitting(true);
        try {
            await createBooking(form);
            toast.success("Request sent! Shawn will hit you back within 24 hrs.", {
                description: `Thanks ${form.name.split(" ")[0]} — we got your idea logged.`,
            });
            setForm(initialForm);
        } catch (err) {
            toast.error("Couldn't send your request. DM @tatzbyb4nk on IG instead.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass =
        "w-full bg-black border border-white/10 text-white placeholder:text-zinc-600 px-4 py-3 focus:outline-none focus:border-[#7CFC00] focus:ring-1 focus:ring-[#7CFC00] transition-colors";

    return (
        <section
            id="booking"
            data-testid="booking-section"
            className="relative py-24 md:py-32 bg-[#0a0a0a] overflow-hidden"
        >
            <div className="absolute inset-0 grid-lines-bg opacity-30 pointer-events-none" />
            <div className="absolute right-0 top-0 hidden lg:block opacity-[0.06] pointer-events-none">
                <div className="font-display text-[14rem] leading-none stencil-stroke">BOOK</div>
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-px w-10 bg-[#7CFC00]" />
                        <span className="text-[#7CFC00] uppercase tracking-[0.3em] text-xs font-semibold">
                            Booking
                        </span>
                    </div>
                    <h2
                        data-testid="booking-title"
                        className="font-display text-5xl md:text-7xl text-white uppercase tracking-tight leading-[0.95]"
                    >
                        Book Your <span className="text-[#7CFC00] text-glow">Session</span>
                    </h2>
                    <p className="mt-4 text-zinc-400 text-base md:text-lg max-w-2xl">
                        Drop your idea below, hit Booksy direct, or slide in the IG DMs. Whatever's faster for you.
                    </p>
                </div>

                <div className="mt-12 grid lg:grid-cols-12 gap-6">
                    {/* Form */}
                    <form
                        data-testid="booking-form"
                        onSubmit={onSubmit}
                        className="lg:col-span-7 bg-black border border-white/10 p-6 md:p-10 space-y-5"
                    >
                        <div className="grid md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-2">
                                    Your Name *
                                </label>
                                <input
                                    data-testid="booking-name"
                                    type="text"
                                    name="name"
                                    value={form.name}
                                    onChange={onChange}
                                    className={inputClass}
                                    placeholder="Marcus T."
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-2">
                                    Email *
                                </label>
                                <input
                                    data-testid="booking-email"
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={onChange}
                                    className={inputClass}
                                    placeholder="you@email.com"
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-2">
                                    Phone
                                </label>
                                <input
                                    data-testid="booking-phone"
                                    type="tel"
                                    name="phone"
                                    value={form.phone}
                                    onChange={onChange}
                                    className={inputClass}
                                    placeholder="(214) 555-0199"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-2">
                                    Budget
                                </label>
                                <select
                                    data-testid="booking-budget"
                                    name="budget"
                                    value={form.budget}
                                    onChange={onChange}
                                    className={inputClass}
                                >
                                    <option value="">— Pick a range —</option>
                                    <option value="$125-200">$125 – $200</option>
                                    <option value="$200-400">$200 – $400</option>
                                    <option value="$400-800">$400 – $800</option>
                                    <option value="$800+">$800+</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-2">
                                    Placement
                                </label>
                                <input
                                    data-testid="booking-placement"
                                    type="text"
                                    name="placement"
                                    value={form.placement}
                                    onChange={onChange}
                                    className={inputClass}
                                    placeholder="Forearm, ribs, calf..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-2">
                                    Size
                                </label>
                                <input
                                    data-testid="booking-size"
                                    type="text"
                                    name="size"
                                    value={form.size}
                                    onChange={onChange}
                                    className={inputClass}
                                    placeholder="3 inch, palm sized, sleeve..."
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs uppercase tracking-widest text-zinc-400 mb-2">
                                Tattoo Idea *
                            </label>
                            <textarea
                                data-testid="booking-idea"
                                name="idea"
                                value={form.idea}
                                onChange={onChange}
                                className={`${inputClass} min-h-[140px] resize-y`}
                                placeholder="Describe what you're thinking — references, vibe, style. The more detail, the better the sketch."
                                required
                            />
                        </div>

                        <button
                            data-testid="booking-submit"
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-[#7CFC00] text-black uppercase font-bold tracking-widest text-sm px-7 py-4 hover:bg-[#65E000] disabled:opacity-60 disabled:cursor-not-allowed transition-colors glow-neon flex items-center justify-center gap-3"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    Send Request <Send size={16} />
                                </>
                            )}
                        </button>
                        <p className="text-xs text-zinc-500 text-center">
                            We respond within 24 hrs. Deposit required to lock in your date.
                        </p>
                    </form>

                    {/* Side CTAs */}
                    <div className="lg:col-span-5 space-y-4">
                        <a
                            data-testid="booksy-cta"
                            href={BOOKSY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block bg-black border border-white/10 p-6 md:p-8 hover:border-[#7CFC00] transition-all hover:-translate-y-1"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
                                        Direct Booking
                                    </div>
                                    <div className="font-display text-3xl md:text-4xl text-white uppercase">
                                        Booksy
                                    </div>
                                    <p className="mt-2 text-zinc-400 text-sm">
                                        See live availability and pick your slot. Auto-confirms instantly.
                                    </p>
                                </div>
                                <div className="bg-[#7CFC00] text-black p-3 group-hover:rotate-[-12deg] transition-transform">
                                    <Calendar size={22} />
                                </div>
                            </div>
                            <div className="mt-5 text-[#7CFC00] uppercase text-xs tracking-widest font-bold flex items-center gap-2">
                                Open Booksy →
                            </div>
                        </a>

                        <a
                            data-testid="ig-cta"
                            href={IG_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block bg-black border border-white/10 p-6 md:p-8 hover:border-[#7CFC00] transition-all hover:-translate-y-1"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">
                                        DM Direct
                                    </div>
                                    <div className="font-display text-3xl md:text-4xl text-white uppercase">
                                        Instagram
                                    </div>
                                    <p className="mt-2 text-zinc-400 text-sm">
                                        Quick chat, send refs, lock in a vibe before you book.
                                    </p>
                                </div>
                                <div className="bg-[#7CFC00] text-black p-3 group-hover:rotate-[12deg] transition-transform">
                                    <Instagram size={22} />
                                </div>
                            </div>
                            <div className="mt-5 text-[#7CFC00] uppercase text-xs tracking-widest font-bold">
                                @tatzbyb4nk →
                            </div>
                        </a>

                        <div className="bg-gradient-to-br from-[#0c1a04] to-black border border-[#7CFC00]/30 p-6 md:p-8">
                            <div className="font-script text-3xl text-[#7CFC00]">Studio Hours</div>
                            <div className="mt-3 text-zinc-300 text-sm space-y-1">
                                <div className="flex justify-between"><span>Tue – Fri</span><span>11am – 9pm</span></div>
                                <div className="flex justify-between"><span>Saturday</span><span>12pm – 10pm</span></div>
                                <div className="flex justify-between"><span>Sunday</span><span>By appt</span></div>
                                <div className="flex justify-between text-zinc-500"><span>Monday</span><span>Closed</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
